import { CheckCircle2, Info, Library, ShieldCheck } from "lucide-react";
import { Link } from "react-router-dom";

import { SEO } from "@/components/common/SEO";
import { PageHero } from "@/components/sections/PageHero";
import { CTABand } from "@/components/sections/CTABand";
import { Container } from "@/components/ui/Container";
import { Section } from "@/components/ui/Section";
import { GCP_ARTICLE } from "@/data/blog";

export default function BlogArticle() {
  return (
    <>
      <SEO
        title={GCP_ARTICLE.title}
        description="Learn why current GCP certification matters for participant safety, data integrity, regulatory compliance, and clinical research quality."
        path={`/blogs/${GCP_ARTICLE.slug}`}
      />
      <PageHero
        eyebrow="Good Clinical Practice"
        title={GCP_ARTICLE.title}
        description="An educational resource from MedCore Research"
      />

      <Section data-testid="gcp-article">
        <Container>
          <article className="mx-auto max-w-3xl">
            <Link
              to="/blogs"
              className="text-sm font-semibold text-secondary transition-colors hover:text-primary"
            >
              &larr; Back to all articles
            </Link>

            <div className="mt-8 space-y-5 text-base leading-8 text-muted-foreground sm:text-lg">
              {GCP_ARTICLE.introduction.map((paragraph) => (
                <p key={paragraph.slice(0, 32)}>{paragraph}</p>
              ))}
            </div>

            <section className="mt-12" aria-labelledby="gcp-importance">
              <h2
                id="gcp-importance"
                className="text-3xl font-semibold tracking-tight text-foreground"
              >
                {GCP_ARTICLE.importanceHeading}
              </h2>
              <p className="mt-4 leading-7 text-muted-foreground">
                {GCP_ARTICLE.importanceIntro}
              </p>
              <ul className="mt-7 space-y-4">
                {GCP_ARTICLE.benefits.map((benefit) => (
                  <li
                    key={benefit.title}
                    className="flex gap-3 rounded-xl border border-border bg-card p-4"
                  >
                    <CheckCircle2
                      size={21}
                      className="mt-0.5 shrink-0 text-primary"
                      aria-hidden="true"
                    />
                    <p className="leading-7 text-muted-foreground">
                      <strong className="font-semibold text-foreground">
                        {benefit.title}
                      </strong>{" "}
                      {benefit.description}
                    </p>
                  </li>
                ))}
              </ul>
              <div className="mt-7 flex gap-3 rounded-xl bg-primary/5 p-5">
                <ShieldCheck
                  size={23}
                  className="mt-0.5 shrink-0 text-primary"
                  aria-hidden="true"
                />
                <p className="leading-7 text-foreground">
                  {GCP_ARTICLE.requirement}
                </p>
              </div>
            </section>

            <section className="mt-12" aria-labelledby="medcore-commitment">
              <h2
                id="medcore-commitment"
                className="text-3xl font-semibold tracking-tight text-foreground"
              >
                {GCP_ARTICLE.commitmentHeading}
              </h2>
              <div className="mt-5 space-y-5 leading-8 text-muted-foreground">
                {GCP_ARTICLE.commitment.map((paragraph) => (
                  <p key={paragraph.slice(0, 32)}>{paragraph}</p>
                ))}
              </div>
            </section>

            <section
              className="mt-12 border-t border-border pt-8"
              aria-labelledby="references"
            >
              <div className="flex items-center gap-2">
                <Library size={21} className="text-secondary" aria-hidden="true" />
                <h2 id="references" className="text-2xl font-semibold text-foreground">
                  References
                </h2>
              </div>
              <ol className="mt-5 list-decimal space-y-3 pl-5 text-sm leading-6 text-muted-foreground">
                {GCP_ARTICLE.references.map((reference) => (
                  <li key={reference.title}>
                    <span className="font-semibold text-foreground">
                      {reference.organization}.
                    </span>{" "}
                    <cite>{reference.title}.</cite>
                  </li>
                ))}
              </ol>
            </section>

            <aside
              className="mt-10 rounded-xl border border-secondary/20 bg-secondary/5 p-5"
              aria-label="AI-generated content disclaimer"
            >
              <div className="flex items-center gap-2">
                <Info size={19} className="text-secondary" aria-hidden="true" />
                <h2 className="text-base font-semibold text-foreground">
                  AI-Generated Content Disclaimer
                </h2>
              </div>
              <p className="mt-3 text-sm leading-6 text-muted-foreground">
                {GCP_ARTICLE.disclaimer}
              </p>
            </aside>
          </article>
        </Container>
      </Section>

      <CTABand />
    </>
  );
}
